import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CreditCard, Storefront } from "@phosphor-icons/react";

export default function SuperPayments() {
  const [platform, setPlatform] = useState(null);
  const [stores, setStores] = useState([]);
  const [savingId, setSavingId] = useState("");
  const [savingPlatform, setSavingPlatform] = useState(false);

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get("/admin/platform-settings"),
      api.get("/admin/stores"),
    ]);
    setPlatform(p.data);
    setStores(s.data);
  };
  useEffect(() => { load(); }, []);

  const togglePlatform = async () => {
    if (!platform) return;
    setSavingPlatform(true);
    try {
      const next = !platform.online_payment_enabled;
      const r = await api.patch("/admin/platform-settings", { online_payment_enabled: next });
      setPlatform(r.data);
      toast.success(next ? "Online payment enabled across platform" : "Online payment disabled across platform");
    } catch {
      toast.error("Failed to update platform setting");
    }
    setSavingPlatform(false);
  };

  const toggleStore = async (store) => {
    setSavingId(store.id);
    try {
      const next = !(store.online_payment_enabled !== false);
      await api.patch(`/admin/stores/${store.id}/payment`, { online_payment_enabled: next });
      setStores((prev) => prev.map((s) => s.id === store.id ? { ...s, online_payment_enabled: next } : s));
      toast.success(`${store.name}: online payment ${next ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Failed to update seller setting");
    }
    setSavingId("");
  };

  if (!platform) return <div className="p-8 text-gray-500">Loading…</div>;

  const platformOn = !!platform.online_payment_enabled;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">Payment Settings</h1>
        <p className="text-gray-600 text-sm">Control online payments at the platform and seller level. COD is always available and unaffected.</p>
      </div>

      {/* Master toggle */}
      <div className="qd-card p-5">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${platformOn ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
            <CreditCard weight="fill" size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-heading font-black text-lg">Online Payment Across Platform</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {platformOn
                    ? "ON — sellers with online payment enabled will accept online payments."
                    : "OFF — online payment is disabled for ALL sellers. Customers will only see COD."}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={platformOn}
                disabled={savingPlatform}
                onClick={togglePlatform}
                data-testid="platform-online-payment-toggle"
                className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${platformOn ? "bg-green-600" : "bg-gray-300"} disabled:opacity-60`}
              >
                <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${platformOn ? "translate-x-5" : "translate-x-0"}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sellers list */}
      <div className="qd-card overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
          <Storefront weight="duotone" size={18} className="text-gray-500" />
          <span className="font-heading font-bold text-base">Sellers ({stores.length})</span>
        </div>
        {!platformOn && (
          <div className="px-5 py-2 bg-amber-50 text-amber-800 text-xs border-b border-amber-100">
            Platform-level online payment is OFF. Per-seller toggles below are still editable but will have no effect on checkout until the master toggle is ON.
          </div>
        )}
        <div className="divide-y divide-gray-50">
          {stores.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-gray-500">No sellers yet</div>
          )}
          {stores.map((s) => {
            const storeOn = s.online_payment_enabled !== false;
            const effective = platformOn && storeOn;
            return (
              <div key={s.id} className="px-5 py-3 flex items-center gap-3" data-testid={`seller-payment-row-${s.slug}`}>
                <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden">
                  {s.logo ? <img src={s.logo} alt="" className="w-full h-full object-cover" /> : <Storefront weight="duotone" size={20} className="text-gray-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{s.name}</div>
                  <div className="text-[11px] text-gray-500 font-mono truncate">/{s.slug}</div>
                </div>
                <div className="text-right">
                  <div className={`text-[10px] font-bold uppercase tracking-widest ${effective ? "text-green-700" : "text-red-600"}`}>
                    {effective ? "Online ON" : "COD only"}
                  </div>
                  <div className="text-[10px] text-gray-400">
                    {storeOn ? "seller ON" : "seller OFF"}
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={storeOn}
                  disabled={savingId === s.id}
                  onClick={() => toggleStore(s)}
                  data-testid={`seller-online-payment-${s.slug}`}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${storeOn ? "bg-green-600" : "bg-gray-300"} disabled:opacity-60`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${storeOn ? "translate-x-5" : "translate-x-0"}`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
